import GameManager from '../../cvp-webgames-core/GameManager';
import BaseScenarioPart from '../../cvp-webgames-core/Scenario/BaseScenarioPart';
import { DaarcBaseScenario } from '../DaarcBaseScenario';
import BasePracticeScenario from '../base-practice-scenario/BasePracticeScenario';
import { constants } from '../../misc/constants';
import { DebriefIntro, DebriefVortex } from '../../components/ui/DebriefUI';
import { AssessAirwayActionData, AssessAirwayActions } from '../../components/ui/AssessAirwayUI';
import { VortexUICurrentAttempt } from '../../components/ui/hud/VortexUI';

export default class AngelaScott extends BasePracticeScenario {

	public isVideoScope: boolean;
	public isOPA: boolean;
	public isOpaString: any;
	public isHumidityString: string;

	public get maxScore(): number {
		return 52600;
	}

	public get predeterminedPoints(): number {
		var assessPatientRemoveDenturesPoints = 500;
		var setupFiberOpticsPoints = 1000;
		var prePoints = 1900;
		var awardedPoints = 32700;

		return assessPatientRemoveDenturesPoints + prePoints +
			setupFiberOpticsPoints + awardedPoints;
	}

	public get totalNumberOfQuestions(): number {
		return 5;
	}

	public get currentLevel(): number {
		return 3;
	}

	public getDefaultApneaTime(): number {
		return 210
	}

	public getDefaultSa02(): number {
		return 89
	}

	constructor(gameManager: GameManager, scenarioName: string) {
		super(gameManager, scenarioName);

		this._setupIntro();
		this._setupGeneralNeutralState();
		this._setupProcedures();
	}

	public getDebriefIntro(): DebriefIntro {
		return {
			items: [
				"In cases of radiation, burns, scars, or any case of small oral aperture, don’t assume that NMJB will increase oral aperture.",
                "The NMJB will not be an effective optimization for intubation or supraglottic airway as her oral aperature is mechanically restricted.",
                "In cases where VL/DL intubation and supraglottic airway placement are anatomically impossible, frequently bag valve mask oxygenation is still possible and should be utilized until a more definitive airway can be established.",
                "Did you factor in the clinical condition of the patient in your decision to not utilize multiple techniques and optimizations?"
			],
			link: 'https://google.com'
		};
	}

	public getDebriefVortex(): DebriefVortex {
		return {
			successItems: [
				'BVM: ' + this.bvmAttemptCount + ' attempt(s)',
				'ETT: ' + this.ettAttemptCount + ' attempt(s)',
				'SGA: ' + this.sgaAttemptCount + ' attempt(s)',
				'Cricothyrotomy: ' + this.cricAttemptCount + ' attempt(s)'
			],
			askYourselfItems: [
				'Did you exceed the maximum number of optimizations (3) for a given attempt?',
				'Did you optimize between each attempt at the bag valve mask, intubation and supraglottic airway?',
				'Did you follow the Vortex approach by not returning to a technique that you had already attempted with appropriate optimizations and failed?',
				'How can you more efficiently manage the airway? Remember that the quicker you establish a stable, patent airway, the better the patient outcome.'
			]
		};
	}

	public onBVMFinished(): void {
		if(this.isVideoScope && this.isOPA) {
			this.addScore(this.predeterminedPoints);
			this._CheckOxy();
			this.showSuccessPopUpAfterAuscultate(VortexUICurrentAttempt.BVM, 'You were able to get Angela Scott into the green zone by using a one-hand or two-hand bag valve mask with an OPA. Ms. Scott was apneic for ' + this.apneaTimeElapsed + ' while you managed her airway. You kept Ms. Scott in the green zone until the team with the video laryngoscope arrived. The team then established a stable, patent airway for her. You earned ' + this.totalScore + ' out of a possible ' + this.maxScore + ' points and unlocked a cricothyrotomy kit. Review the debrief to help you prepare for additional scenarios and to see how you can improve your score.', 'Good Job on this Scenario', './assets/images/success/ScalpalIcon.png');
		} else this.showFailedAttemptPopUpAfterAuscultate(VortexUICurrentAttempt.BVM, 'A patent airway has not been achieved. Continue attempts at managing the airway.', 'Failed Attempt');
	}

	public onETTFinished(): void {
		this.showFailedAttemptPopUpAfterAuscultate(VortexUICurrentAttempt.ETT, 'A patent airway has not been achieved. Continue attempts at managing the airway.', 'Failed Attempt');
	}

	public onSGAFinished(): void {
		this.showFailedAttemptPopUpAfterAuscultate(VortexUICurrentAttempt.SGA, 'A patent airway has not been achieved. Continue attempts at managing the airway.', 'Failed Attempt');
	}

	public _CheckOxy() {
		if(!this.preOxDone) {
			this.debriefLogFailedItem("Pre-Oxygenation: You did not pre-oxygenate the patient.")
		};
		if(!this.callFlexBr) {
			this.debriefLogFailedItem("Call for flexible bronchoscope: Recognizing the need for a flexible bronchoscope early is critical, especially when the patient’s condition indicates intubation and use of a supraglottic airway will be anatomically impossible.")
		}	
	}

	public getAssessAirwayData(): AssessAirwayActionData[] {
		return [
			{
				relatedAction: AssessAirwayActions.ExamineNeck,
				title: 'Examine Neck',
				text: 'There are burn scars that extend from her zygomatic arch to her mandible bilaterally. The external larynx is scar free, mobile, and midline. You can palpate the thyroid and cricoid cartilage as well as the cricothyroid membrane.'
			},
			{
				relatedAction: AssessAirwayActions.LiftChinToCeiling,
				title: 'Lift chin to ceiling',
				text: 'Thyromental Distance: 4 finger breadths (6cm) <br /> Hyoid to Thyroid Notch Distance: 2 finger breadths (3cm)'
			},
			{
				relatedAction: AssessAirwayActions.PutChinToChest,
				title: 'Put chin to chest',
				text: 'No feedback to display'
			},
			{
				relatedAction: AssessAirwayActions.RemoveDentures,
				title: 'Remove Dentures',
				text: 'The patient is not wearing dentures',
				noPoints: true
			},
			{
				relatedAction: AssessAirwayActions.ShowTeeth,
				title: 'Smile for me',
				text: 'Yes'
			},
			{
				relatedAction: AssessAirwayActions.StickOutTongue,
				title: 'Open mouth',
				text: 'Inter-incisor Distance: 1 finger breadths (1.5 cm)',
				imgUrl:'./assets/images/assess-airway-images/Class4SmallOralAperture.png'
			}
		]
	}

	private _setupIntro() {
		// this.intro.config.part1Config = {
		// 	videoUrl: './assets/videos/scenario12/daarc-scenario12-angelascott-v1.mp4',
		// 	dialogs: [
		// 		{
		// 			speakerName: constants.speakers.RnMiranda,
		// 			text: `
		// 			Patient name is Angela Scott. She’s a 65-year-old female, 68 in, 80 kg. 
		// 			Her K is 3.8 and she has not eaten breakfast this morning. 
		// 			She had third degree burns 20 years ago, including on her face.
		// 			`,
		// 			VideoChapter: () =>'DAARC - Scenario12 - intro - 53'
		// 		},
		// 		{
		// 			speakerName: constants.speakers.DrCaleb,
		// 			text: `
		// 			Patient currently has severe pneumonia with a large infiltrate and is now in pending respiratory failure. 
		// 			She is febrile. I’ll manage the
		// 				hemodynamics, you manage the airway.
		// 			`,
		// 			VideoChapter: () =>'DAARC - Scenario12 - intro - 53'
		// 		}
		// 	],
		// 	sbarConfig: {
		// 		s: `Angela Scott came in with severe pneumonia with a large infiltrate and impending respiratory failure. She is febrile. She had third degree burns 20 years ago, including on her face.`,
		// 		b: `
		// 			Age: 65<br />
		// 			Height (in): 68 in<br />
		// 			Weight (kg): 80 kg<br />
		// 			Potassium (K): 3.8<br />
		// 			NPO Status: NPO<br />
		// 			<br />
		// 			PMH: Third degree burns 20 years ago, including face. Now has pneumonia with impending respiratory collapse.
		// 			`,
		// 		a: `Patient has severe pneumonia with a large infiltrate and is in respiratory extremis.`,
		// 		r: `This patient needs to be intubated for impending respiratory failure.`
		// 	}
		// }

		this._id_ScenarioSet(12);

		this.intro.config.part1Config = {
			videoUrl: './assets/videos/scenario12/daarc-scenario12-angelascott-v1.mp4',
			chapters: [
				{
					chapterName: () => 'DAARC - Scenario12 - intro - 53'
				}
			],
			sbarConfig: {
				s: `Angela Scott came in with severe pneumonia with a large infiltrate and impending respiratory failure. She is febrile. She had third degree burns 20 years ago, including on her face.`,
				b: `
					Age: 65<br />
					Height (in): 68 in<br />
					Weight (kg): 80 kg<br />
					Potassium (K): 3.8<br />
					NPO Status: NPO<br />
					<br />
					PMH: Third degree burns 20 years ago, including face. Now has pneumonia with impending respiratory collapse.
					`,
				b508: `
					Age. 65.
					Height. 68 inches.
					Weight. 80 kilograms.
					Potassium. 3.8.
					NPO Status. NPO.
					
					Past Medical History. Third degree burns 20 years ago including face. Now has pneumonia with impending respiratory collapse.
					`,
				a: `Patient has severe pneumonia with a large infiltrate and is in respiratory extremis.`,
				r: `This patient needs to be intubated for impending respiratory failure.`
			}
		}
	}

	private _setupGeneralNeutralState() {
		this.neutralState.config.part1Config = {
			videoUrl: './assets/videos/scenario12/daarc-scenario12-angelascott-v1.mp4',
			hudIdleVideoChapter: () =>'DAARC - Scenario12 - Flat - Nothing - 1',
			onCalledForVortex: () => {
				this.debriefLog('Called for a Vortex reader')
			}
		};
		var toolsArr = null
		if(this.isLite) {
			toolsArr = [
				constants.toolbagTools.bvm,
				constants.toolbagTools.ett,
				constants.toolbagTools.sga,	
				//constants.toolbagTools.cric //comment this out on cric not required scenarios		
			]
		} else {			
				toolsArr = [
				constants.toolbagTools.bvm,
				constants.toolbagTools.ett,
				constants.toolbagTools.sga,
				//constants.toolbagTools.cric
			]
		}

		this.neutralState.config.part2Config = {
			videoUrl: './assets/videos/scenario12/daarc-scenario12-angelascott-v1.mp4',
			toolsToChooseFrom: toolsArr,
			onTeamNotDebriefed: () => {
				this.debriefLogFailedItem("Brief other staff in the room about attempt order: Briefing your staff on the attempt order will help them anticipate providing the right equipment. Briefings empower your staff to notify you if you fall into fixation error.")
            },
            onTeamDebriefed: () => {
                this.debriefLog('Briefed other staff in the room about attempt order');
            },
            onToolsOrganized: () => {
                this.debriefLog('Organized equipment');
            }
		};

		this.neutralState.config.part3Config = {
			videoUrl: './assets/videos/scenario12/daarc-scenario12-angelascott-v1.mp4',
			waitForSuctionVideoChapter: () =>'DAARC - Scenario12 - Flat - Nothing - 1',
			onSuctionSet: () => {
				this.debriefLog("Suction Set") 
			}
		};

		this.neutralState.config.part4Config = {
			videoUrl: './assets/videos/scenario12/daarc-scenario12-angelascott-v1.mp4',
			waitForAdjustBedHeightVideoChapter: () =>'DAARC - Scenario12 - 45 - Nothing - 7',
			onAdjustedBedHeight: () => {
				this._id_bedPos = this._id_bedAdj;
				this.debriefLog("Bed Adjusted");
			}
		};

		this.neutralState.config.part5Config = {
			videoUrl: './assets/videos/scenario12/daarc-scenario12-angelascott-v1.mp4',
			liftChinVideoChapter: () =>'DAARC - Scenario12 - 45 - patient-assessment-up - 36',
			chinToChestVideoChapter: () =>'DAARC - Scenario12 - 45 - patient-assessment-down - 37',
			examineNeckVideoChapter: () =>'DAARC - Scenario12 - 45 - patient-assessment-straight - 35',
			realTeethVideoChapter: () =>'DAARC - Scenario12 - 45 - patient-assessment-straight - 35',
			stickOutTongueVideoChapter: () =>'DAARC - Scenario12 - 45 - patient-assessment-straight - 35',
			onAssessmentFinied: () => {
				this.debriefLog("Performed a complete airway assessment")
			}
		};

		this.neutralState.config.part6Config = {
			videoUrl: './assets/videos/scenario12/daarc-scenario12-angelascott-v1.mp4',
			waitForFiberOpticVideoChapter: () => this._id_SegmentAssembly(this._id_preOxText),
			onSetupFiberOptic: () => {
				this.isVideoScope = true;
				this.callFlexBr = true;
				this.debriefLog("Called for advanced airway cart");
			},
			notificationText: 'The VL/FBS is available for use.'
		};

		this.neutralState.config.part7Config = {
			videoUrl: './assets/videos/scenario12/daarc-scenario12-angelascott-v1.mp4',
			waitForPreOxygenateVideoChapter: () => this._id_SegmentAssembly(this._id_preOxText),
			onPreOxygenated: (selection: number) => {
				this.preOxDone = true;
				this._id_UpdatePreOxText(selection);
				switch(selection) {
					case 0:
						this.debriefLog("Pre-Oxygenated with High Flow Nasal Cannula");
						break;
					case 1:
						this.debriefLog("Pre-Oxygenated with Nasal Noninvasive Positive Pressure");
						break;
					case 2:
						this.debriefLog("Pre-Oxygenated with 100% Non-Rebreather Mask");
						break;
					case 3:
						this.debriefLogFailedItem("Pre-Oxygenated with BVM. This may not be the best solution");
						break;
				}
			}
		};

		this.neutralState.config.part8Config = {
			videoUrl: './assets/videos/scenario12/daarc-scenario12-angelascott-v1.mp4',
			meds: [
				{
					title: 'Induction Agent',
					audioPrefix: 'InductionAgent_'
				},
				{
					title: 'Administer Neuromuscular Junction Blocker',
					audioPrefix: 'NMJB_'
				}
			],
			onApneaStartedVideoChapter: () => this._id_SegmentAssembly(this._id_preOxText), // APNEA TIMER INPUT SEGMENT CHANGE HERE,
			onPatientFullyMedicated: () => {	
				this.debriefSetAdministeringText("You correctly medicated your patient and earned maximum points.")
			},
			onSingleMedAdministered: () => {
				this.debriefSetAdministeringText("Did you administer all medications that are appropriate, given the patient's condition?") //May need more description			
			}
		};
	}

	private _setupProcedures() {
		this._setupBvm();
		this._setupEtt();
		this._setupSga();
		this._setupAuscultate();
	}

	private _setupBvm() {
		this.procedures.bvm.config.isAttemptSuccessful = () => {
			if(this.isVideoScope && this.isOPA) {
				return true;
			} else return false;
		};

		this.procedures.bvm.config.onMakeAttemptClicked = (isOneHanded: boolean, isOPA: boolean, isNPA: boolean, isManipulateHeadAndNeck: boolean) => {
			this.isOPA = isOPA;
			if(this.isOPA) {
				this.isOpaString = this.hoverBreathYes;
				this.isHumidityString = this.auscSuccess;
			} else {
				this.isOpaString = this.hoverBreathNo;
				this.isHumidityString = this.auscFailed;
			};
		};

		this.procedures.bvm.config.part1Config = {
			videoUrl: './assets/videos/scenario12/daarc-scenario12-angelascott-v1.mp4',
			chooseHandVideoChapter: () => this._id_bvmHandAttempt,
			onMakeAttemptClicked: (isNPAClicked: boolean, isOPAClicked: boolean) => {
				this.isOPA = isOPAClicked;
				if(this.isOPA) {
					this.isOpaString = this.hoverBreathYes;
					this.isHumidityString = this.auscSuccess;
				} else {
					this.isOpaString = this.hoverBreathNo;
					this.isHumidityString = this.auscFailed;
				};
			},
			onChooseHandNextClicked: (isOneHanded: boolean) => {
				if(isOneHanded) {
					this._id_bvmHandAttempt = this._id_SegmentAssembly(this._id_BVMAttempt(1)) // set segment variable equal to this
				} else {
					this._id_bvmHandAttempt = this._id_SegmentAssembly(this._id_BVMAttempt(2)) // set segment variable equal to this
				}
			}
		};
		this.procedures.bvm.config.part2Config = {
			videoUrl: './assets/videos/scenario12/daarc-scenario12-angelascott-v1.mp4',
			onProcedureAttempted: (attemptCount: number) => {
				this.bvmAttemptCount = attemptCount;
			}
		}
	}

	private _setupEtt() {
		this.procedures.ett.config.part1Config = {
			hideVlButton: () => !this.callFlexBr, // If Neutral step 6 has not been performed, the VL button should be hidden			
			videoUrl: './assets/videos/scenario12/daarc-scenario12-angelascott-v1.mp4',
			squeezeAmbuBagVideoChapter: () => this._id_SegmentAssembly(this._id_ETTAttempt()),
			insertingETTVideoChapter: () => this._id_SegmentAssembly(this._id_ETTAttempt()),
			ettSpecialUIConfig: {
				airwayImage: () => {
					return './assets/images/ett/HardPinkPallet.png';
				},
				magillForceps: {
					text: () => 'There is no foreign body in the patient’s airway.'
				},
				suction: {
					text: () => ''
				},
				manipulateLarynx: {
					text: () => 'Due to the small oral aperture, the laryngoscope does not fit in the patient\'s mouth. There is no useful view of the airway.'
				}
			},
			onProcedureAttempted: (attemptCount: number) => {
				this.ettAttemptCount = attemptCount;
			}
		};
	}

	private _setupSga() {
		this.procedures.sga.config.part1Config = {
			isAngelaScott: true,
			videoUrl: './assets/videos/scenario12/daarc-scenario12-angelascott-v1.mp4',
			squeezeAmbuBagVideoChapter: () =>this._id_SegmentAssembly(this._id_SGAAttempt()),
			insertingSgaVideoChapter: () =>this._id_SegmentAssembly(this._id_SGAAttempt()),
			onProcedureAttempted: (attemptCount: number) => {
				this.sgaAttemptCount = attemptCount;
			}
		};
	}

	private _setupAuscultate() {
		if(this.isVideoScope && this.isOPA) {
			this.isOpaString = this.hoverBreathYes;
			this.isHumidityString = this.auscSuccess;
		} else {
			this.isOpaString = this.hoverBreathNo;
			this.isHumidityString = this.auscFailed;
		};
		this.procedures.auscultate.config.part1Config = {
			videoUrl: './assets/videos/scenario12/daarc-scenario12-angelascott-v1.mp4',
			bvmConfig: {
				moveToAuscultateScreenVideoChapter: () => this._id_SegmentAssembly(this._id_bvmAusc),
				checkRiseAndTubeHumidificationText: () => this.isHumidityString,
				audioPath: () => {
					if(this.isVideoScope && this.isOPA) {
						return './assets/audio/auscultate/vortexreaderreminder_success2.mp3';
					} else return './assets/audio/auscultate/vortexreaderreminder_fail.mp3';
				},
				isAnswerCorrect: (isYesClicked: boolean) => {
					if(this.isVideoScope && this.isOPA) {
						return isYesClicked;
					} else return !isYesClicked
				},
				auscultateHoverOverMessages: () => {
					return this.isOpaString
				}
			},
			ettConfig: {
				moveToAuscultateScreenVideoChapter: () => this._id_SegmentAssembly(this._id_ettAusc),
				checkRiseAndTubeHumidificationText: () => this.auscFailed,
				audioPath: () => {
					return './assets/audio/auscultate/vortexreaderreminder_fail.mp3';
				},
				isAnswerCorrect: (isYesClicked: boolean) => {
					return !isYesClicked;
				},
				auscultateHoverOverMessages: () => {
					return this.hoverBreathNo
				}
			},
			sgaConfig: {
				moveToAuscultateScreenVideoChapter: () => this._id_SegmentAssembly(this._id_sgaAusc),
				checkRiseAndTubeHumidificationText: () => this.auscFailed,
				audioPath: () => {
					return './assets/audio/auscultate/vortexreaderreminder_fail.mp3';
				},
				isAnswerCorrect: (isYesClicked: boolean) => {
					return !isYesClicked;
				},
				auscultateHoverOverMessages: () => {
					return this.hoverBreathNo
				}
			},
			cricConfig: {
				moveToAuscultateScreenVideoChapter: () => this._id_SegmentAssembly(this._id_cricAusc, 'flat'),
				checkRiseAndTubeHumidificationText: () => this.auscSuccess,
				audioPath: () => {
					return './assets/audio/auscultate/vortexreaderreminder_success2.mp3';
				},
				isAnswerCorrect: (isYesClicked: boolean) => {
					return isYesClicked;
				},
				auscultateHoverOverMessages: () => {
					return this.hoverBreathYes
				}
			}
		}
	}


	public getParts(): BaseScenarioPart<DaarcBaseScenario>[] {
		return this.parts;
	}

	public getSoundPath(fileName: string): string {
		return './assets/audio/scenario1/' + fileName + '.ogg';
	}

	public showDefaultSideMenu() {
		const context = this;
		context.sideMenuId = context.uiWrapper.displaySideMenu({
			gameManager: this.gameManager,
			inductionTimeInSeconds: 30,
			apneaTimeInSeconds: 210,
			bloodPressureNumerator: 110,
			bloodPressureDenominator: 70,
			bloodPressureMinNumerator: 50,
			bloodPressureMinDenominator: 33,
			bloodPressureMaxNumerator: 220,
			bloodPressureMaxDenominator: 110,
			sa02: 89,
			heartRate: 125,
			heartRateMax: 200,
			respirationRate: 28,
			resetEtc02: false,
			etc02: 35,
			showETC02: false,
			isApneaTimerIdle: true,
			onInductionTimerRanOut: () => {
				context.hasInductionTimerRanOut = true;
			},
			onApneaTimerRanOut: () => {
                context.failedScenario = true;
				this._CheckOxy();
				context.onShowSuccessFailedAttempt(constants.attemptText.failedTitle, 'You did not establish a stable, patent airway in time for Angela Scott. She has expired. Review the debrief and then try again.', true);
			}
		}, context.sideMenuId);
	}
}